import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, GripVertical, Mail, CheckCircle2, AlertCircle, Settings as SettingsIcon, Archive, ArchiveRestore, Users as UsersIcon, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../lib/settings";
import { useConfirm } from "../components/ConfirmModal";
import PragmaSelect from "../components/PragmaSelect";
import GmailSettingsModal from "../components/GmailSettingsModal";
import GmailConnectedCelebration from "../components/GmailConnectedCelebration";
import ImportsTab from "./ImportsTab";
import ProposalBrandingTab from "./ProposalBrandingTab";
import AssetLibrary from "../components/AssetLibrary";
import WebhooksApiTab from "./WebhooksApiTab";

const ALL_TABS = [
  { k: "company", label: "Company Profile", roles: ["admin"] },
  { k: "users", label: "Users", roles: ["admin"] },
  { k: "pipelines", label: "Pipelines", roles: ["admin", "manager"] },
  { k: "fields", label: "Fields", roles: ["admin", "manager"] },
  { k: "import", label: "Import", roles: ["admin", "manager"] },
  { k: "proposals", label: "Proposal branding", roles: ["admin", "manager"] },
  { k: "assets", label: "Asset library", roles: ["admin", "manager", "sales_rep"] },
  { k: "integrations", label: "Integrations", roles: ["admin", "manager", "sales_rep"] },
  { k: "webhooks", label: "Webhooks & API", roles: ["admin", "manager"] },
  { k: "danger", label: "Delete Account", roles: ["admin"] },
];

export default function Settings() {
  const { user } = useAuth();
  const role = user?.role || "sales_rep";
  const tabs = ALL_TABS.filter(t => t.roles.includes(role));
  const [tab, setTab] = useState(tabs[0]?.k || "company");

  // If URL contains ?tab=integrations or ?google=connected/error, auto-select Integrations tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("tab");
    if (requested && tabs.some(t => t.k === requested)) {
      setTab(requested);
    } else if (params.get("google") && tabs.some(t => t.k === "integrations")) {
      setTab("integrations");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="workspace configuration" />
      <div className="px-8 py-4 flex items-center gap-2 flex-wrap" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        {tabs.map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`pg-btn ${tab === t.k ? "pg-btn-active" : ""}`}
            data-testid={`settings-tab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="p-8">
        {tab === "company" && <CompanyProfileTab />}
        {tab === "users" && <UsersTab />}
        {tab === "pipelines" && <PipelinesTab />}
        {tab === "fields" && <FieldsTab />}
        {tab === "import" && <ImportsTab />}
        {tab === "proposals" && <ProposalBrandingTab />}
        {tab === "assets" && <AssetLibraryTab />}
        {tab === "integrations" && <IntegrationsTab />}
        {tab === "webhooks" && <WebhooksApiTab />}
        {tab === "danger" && <DeleteAccountTab />}
      </div>
    </div>
  );
}

function AssetLibraryTab() {
  return (
    <div style={{ maxWidth: 980 }} data-testid="asset-library-tab">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Asset library</h2>
        <p style={{ fontSize: 13, color: "var(--pg-text-mute)", lineHeight: 1.55 }}>
          A shared workspace for images and PDFs you use across proposals. Everything uploaded from the proposal
          builder lands here automatically — reuse logos, banners, and case-study PDFs without re-uploading.
          Every workspace gets 1 GB of storage.
        </p>
      </div>
      <AssetLibrary mode="manage" accept="any" />
    </div>
  );
}

function CompanyProfileTab() {
  const [form, setForm] = useState({ company_name: "", business_number: "", currency: "USD" });
  const [currencies, setCurrencies] = useState([]);
  const [saving, setSaving] = useState(false);
  const { refresh } = useAuth();
  const { refresh: refreshSettings } = useSettings();

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/settings"), api.get("/currencies")]);
      setForm({
        company_name: s.data.company_name || "",
        business_number: s.data.business_number || "",
        currency: s.data.currency || "USD",
      });
      setCurrencies(c.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Company profile updated");
      await Promise.all([refresh(), refreshSettings()]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="pg-card p-6 max-w-2xl space-y-4" data-testid="company-profile-form">
      <h2 className="text-lg">Company Profile</h2>
      <p className="mono text-[11px] text-mute">Replaces "Pragma" in the top of the sidebar. Currency applies to every monetary value in the app.</p>
      <Lbl label="Company Name"><input className="pg-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required data-testid="company-name" /></Lbl>
      <Lbl label="Business Number (ABN / VAT / EIN)"><input className="pg-input" value={form.business_number} onChange={(e) => setForm({ ...form, business_number: e.target.value })} data-testid="company-business-number" /></Lbl>
      <Lbl label="Default Currency">
        <PragmaSelect
          value={form.currency}
          onChange={(v) => setForm({ ...form, currency: v })}
          options={currencies.map((c) => ({ value: c.code, label: `${c.symbol} — ${c.code}`, hint: c.label }))}
          testId="company-currency"
          searchable
        />
      </Lbl>
      <button type="submit" className="pg-btn pg-btn-primary" disabled={saving} data-testid="company-save">{saving ? "Saving…" : "Save"}</button>
    </form>
  );
}

function Lbl({ label, children }) {
  return (
    <div>
      <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

function UsersTab() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [reassignFor, setReassignFor] = useState(null); // { user, mode: "archive" | "delete" }

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const unarchive = async (u) => {
    try {
      await api.post(`/users/${u.id}/unarchive`);
      toast.success("User restored");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const activeUsers = useMemo(() => users.filter((u) => u.active !== false && !u.archived_at), [users]);
  const archivedUsers = useMemo(() => users.filter((u) => u.archived_at), [users]);

  const inheritorCandidates = useMemo(
    () => activeUsers.filter((u) => u.id !== reassignFor?.user?.id),
    [activeUsers, reassignFor],
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg">Users</h2>
        <button className="pg-btn pg-btn-primary" onClick={() => setShowForm(true)} data-testid="invite-user-btn">
          <Plus size={14} /> Invite User
        </button>
      </div>
      <div className="pg-card overflow-hidden">
        <table className="pg-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Job Title</th><th>Role</th><th>Status</th><th style={{ width: 160 }}></th></tr>
          </thead>
          <tbody>
            {activeUsers.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>{u.name}</td>
                <td className="mono text-xs text-dim">{u.email}</td>
                <td>
                  <input
                    className="pg-input"
                    style={{ width: 180 }}
                    defaultValue={u.job_title || ""}
                    placeholder="e.g. AE, SDR"
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (v === (u.job_title || "")) return;
                      try { await api.patch(`/users/${u.id}`, { job_title: v }); load(); }
                      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); load(); }
                    }}
                    data-testid={`user-job-title-${u.id}`}
                  />
                </td>
                <td>
                  <PragmaSelect
                    value={u.role}
                    onChange={async (v) => {
                      try { await api.patch(`/users/${u.id}`, { role: v }); load(); }
                      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); load(); }
                    }}
                    options={[
                      { value: "admin",     label: "Admin" },
                      { value: "manager",   label: "Manager" },
                      { value: "sales_rep", label: "Sales Rep" },
                    ]}
                    testId={`user-role-${u.id}`}
                    size="sm"
                    style={{ width: 140 }}
                  />
                </td>
                <td>
                  <span className="mono text-[11px] uppercase" style={{ color: "var(--pg-accent-deep)" }}>Active</span>
                </td>
                <td>
                  {u.id !== currentUser?.id && (
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setReassignFor({ user: u, mode: "archive" })}
                        className="pg-btn"
                        title="Archive user & reassign their records"
                        data-testid={`user-archive-${u.id}`}
                      >
                        <Archive size={14} /> Archive
                      </button>
                      <button
                        onClick={() => setReassignFor({ user: u, mode: "delete" })}
                        className="text-mute hover:text-[var(--pg-red)]"
                        title="Delete user permanently & reassign their records"
                        data-testid={`user-delete-${u.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {archivedUsers.length > 0 && (
              <tr>
                <td colSpan={6} style={{ background: "var(--pg-surface-2)", padding: "10px 16px" }}>
                  <span className="mono text-[10px] uppercase text-mute tracking-wider">Archived</span>
                </td>
              </tr>
            )}
            {archivedUsers.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} style={{ opacity: 0.65 }}>
                <td>{u.name}</td>
                <td className="mono text-xs text-dim">{u.email}</td>
                <td className="mono text-xs text-dim">{u.job_title || "—"}</td>
                <td className="mono text-xs text-dim">{u.role}</td>
                <td>
                  <span className="mono text-[11px] uppercase" style={{ color: "var(--pg-text-mute)" }}>Archived</span>
                </td>
                <td>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => unarchive(u)}
                      className="pg-btn"
                      title="Restore user"
                      data-testid={`user-unarchive-${u.id}`}
                    >
                      <ArchiveRestore size={14} /> Restore
                    </button>
                    <button
                      onClick={() => setReassignFor({ user: u, mode: "delete" })}
                      className="text-mute hover:text-[var(--pg-red)]"
                      title="Delete user permanently"
                      data-testid={`user-delete-${u.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <InviteUserForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {reassignFor && (
        <ReassignUserModal
          user={reassignFor.user}
          mode={reassignFor.mode}
          candidates={inheritorCandidates}
          onClose={() => setReassignFor(null)}
          onDone={() => { setReassignFor(null); load(); }}
        />
      )}
    </div>
  );
}

function ReassignUserModal({ user, mode, candidates, onClose, onDone }) {
  const [inheritorId, setInheritorId] = useState(candidates[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const verb = mode === "delete" ? "Delete" : "Archive";
  const heading = mode === "delete" ? "Delete user permanently" : "Archive user";
  const explainer = mode === "delete"
    ? `This will permanently remove ${user.name} from the workspace. All their leads, contacts, deals, and moves will be reassigned to the teammate you pick. History entries stay attributed to ${user.name} for compliance.`
    : `This will deactivate ${user.name} and reassign every one of their leads, contacts, deals, and moves to the teammate you pick. History entries stay attributed to ${user.name}. You can restore them later.`;

  const submit = async () => {
    if (!inheritorId) { toast.error("Pick who inherits these records"); return; }
    setBusy(true);
    try {
      const url = mode === "delete"
        ? `/users/${user.id}?reassign_to=${encodeURIComponent(inheritorId)}`
        : `/users/${user.id}/archive?reassign_to=${encodeURIComponent(inheritorId)}`;
      const res = mode === "delete"
        ? await api.delete(url)
        : await api.post(url);
      const counts = res.data?.reassigned || {};
      const detail = Object.entries(counts).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(", ");
      toast.success(detail ? `${verb}d — reassigned ${detail}` : `${verb}d`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setBusy(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={busy ? undefined : onClose} />
      <div className="side-panel" data-testid="reassign-user-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">{heading}</h2>
          <div className="mono text-[11px] text-mute mt-1">{user.email}</div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div style={{
            background: "var(--pg-surface-2)",
            border: "1px solid var(--pg-border)",
            borderRadius: 8,
            padding: "12px 14px",
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--pg-text-mute)",
          }}>
            {explainer}
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">
              <UsersIcon size={11} className="inline mr-1 -mt-0.5" /> Reassign records to
            </label>
            {candidates.length === 0 ? (
              <div className="mono text-[11px] text-mute">
                No other active teammates available. Invite someone first, then retry.
              </div>
            ) : (
              <PragmaSelect
                value={inheritorId}
                onChange={setInheritorId}
                options={candidates.map((c) => ({ value: c.id, label: c.name, hint: c.role }))}
                testId="reassign-user-inheritor"
              />
            )}
          </div>
          {mode === "delete" && (
            <div className="mono text-[11px]" style={{ color: "var(--pg-red)" }}>
              This action cannot be undone.
            </div>
          )}
        </div>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            onClick={submit}
            className={`pg-btn ${mode === "delete" ? "" : "pg-btn-primary"}`}
            disabled={busy || candidates.length === 0}
            data-testid="reassign-user-submit"
            style={mode === "delete" ? {
              background: "var(--pg-red)",
              color: "#fff",
              borderColor: "var(--pg-red)",
            } : undefined}
          >
            {busy ? "Working…" : `${verb} & reassign`}
          </button>
        </div>
      </div>
    </>
  );
}

function InviteUserForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", role: "sales_rep", job_title: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`Invite sent to ${form.email} · they'll receive a one-click "set password" link`);
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="invite-user-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">Invite teammate</h2>
          <div className="mono text-[11px] text-mute mt-1">we'll email them a link to set their own password</div>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">First Name</label>
              <input className="pg-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="user-input-first-name" />
            </div>
            <div>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Last Name</label>
              <input className="pg-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="user-input-last-name" />
            </div>
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Email</label>
            <input className="pg-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-input-email" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Job Title</label>
            <input
              className="pg-input"
              value={form.job_title}
              onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              placeholder="e.g. Account Executive, SDR, Head of Sales"
              data-testid="user-input-job-title"
            />
            <div className="mono text-[10px] text-mute mt-1">Shown on the user profile — separate from their permission role.</div>
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Role</label>
            <PragmaSelect
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
              options={[
                { value: "sales_rep", label: "Sales Rep" },
                { value: "manager",   label: "Manager" },
                { value: "admin",     label: "Admin" },
              ]}
              testId="user-input-role"
            />
          </div>
          <div className="text-xs" style={{ color: "var(--pg-text-mute)", lineHeight: 1.55, marginTop: 4 }}>
            Your teammate will get a Pragma email with a one-click "set your password" link (expires in 7 days).
            They'll also verify a 6-digit code from their inbox on first sign-in.
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="user-submit">{saving ? "Sending…" : "Send invite"}</button>
        </div>
      </div>
    </>
  );
}

function PipelinesTab() {
  const confirm = useConfirm();
  const [pipelines, setPipelines] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    const { data } = await api.get("/pipelines");
    setPipelines(data);
    if (!active && data[0]) { setActive(data[0].id); setDraft(JSON.parse(JSON.stringify(data[0]))); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectPipeline = (id) => {
    setActive(id);
    const p = pipelines.find(p => p.id === id);
    setDraft(JSON.parse(JSON.stringify(p)));
  };

  const save = async () => {
    try {
      await api.patch(`/pipelines/${draft.id}`, {
        name: draft.name,
        stages: draft.stages.map((s, i) => ({ ...s, order: i, probability: parseFloat(s.probability) || 0 })),
      });
      toast.success("Pipeline saved");
      load();
    } catch { toast.error("Failed"); }
  };

  const addStage = () => {
    setDraft({ ...draft, stages: [...draft.stages, { name: "New Stage", order: draft.stages.length, probability: 50 }] });
  };

  const removeStage = (i) => {
    setDraft({ ...draft, stages: draft.stages.filter((_, idx) => idx !== i) });
  };

  const moveStage = (i, dir) => {
    const newIdx = i + dir;
    if (newIdx < 0 || newIdx >= draft.stages.length) return;
    const arr = [...draft.stages];
    [arr[i], arr[newIdx]] = [arr[newIdx], arr[i]];
    setDraft({ ...draft, stages: arr });
  };

  const newPipeline = async (kind) => {
    const label = kind === "growth" ? "Growth" : "Acquisition";
    const name = await confirm({
      title: `New ${label} pipeline`,
      message: kind === "growth"
        ? "Give this Growth pipeline a name. You'll pick renewals or upsell next."
        : "Give this Acquisition pipeline a name.",
      input: { placeholder: `${label} Pipeline`, required: true, label: "Pipeline name" },
      confirmLabel: "Continue",
    });
    if (!name) return;
    let growth_kind = null;
    if (kind === "growth") {
      const raw = await confirm({
        title: "Growth pipeline type",
        message: "Is this pipeline for renewals or upsell?",
        input: { placeholder: "renewals or upsell", defaultValue: "renewals", required: true, label: "Type" },
        confirmLabel: "Create pipeline",
      });
      const norm = (raw || "").trim().toLowerCase();
      if (norm !== "renewals" && norm !== "upsell") {
        toast.error("Growth pipeline requires 'renewals' or 'upsell'");
        return;
      }
      growth_kind = norm;
    }
    try {
      const { data } = await api.post("/pipelines", {
        name,
        pipeline_kind: kind,
        growth_kind,
        stages: [
          { name: "New", order: 0, probability: 10 },
          { name: "Closed Won", order: 1, probability: 100 },
          { name: "Closed Lost", order: 2, probability: 0 },
        ],
      });
      toast.success("Created"); load(); setActive(data.id); setDraft(data);
    } catch { toast.error("Failed"); }
  };

  const deletePipeline = async () => {
    if (!await confirm({ title: "Delete this pipeline?", message: "Deals using this pipeline must be reassigned first.", destructive: true, confirmLabel: "Delete pipeline" })) return;
    try { await api.delete(`/pipelines/${active}`); toast.success("Deleted"); setActive(null); setDraft(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!draft) return <div className="text-mute mono text-xs">loading…</div>;

  const acquisitionPipelines = pipelines.filter(p => (p.pipeline_kind || "acquisition") === "acquisition");
  const growthPipelines = pipelines.filter(p => p.pipeline_kind === "growth");

  const renderRow = (p) => (
    <button
      key={p.id}
      onClick={() => selectPipeline(p.id)}
      className="w-full text-left px-3 py-2"
      data-testid={`pipeline-row-${p.id}`}
      style={{
        background: active === p.id ? "var(--pg-surface-2)" : "transparent",
        borderLeft: active === p.id ? "2px solid var(--pg-purple)" : "2px solid transparent",
        color: active === p.id ? "var(--pg-text)" : "var(--pg-text-dim)",
        fontSize: 14,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
      {p.pipeline_kind === "growth" && p.growth_kind && (
        <span className="mono text-[9px] uppercase tracking-wider" style={{ color: "var(--pg-text-mute)" }}>
          {p.growth_kind}
        </span>
      )}
    </button>
  );

  return (
    <div className="grid grid-cols-4 gap-6">
      <div className="col-span-1 space-y-6">
        <div>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm mono uppercase text-mute tracking-wider">Acquisition Pipelines</h3>
            <button
              onClick={() => newPipeline("acquisition")}
              className="pg-btn pg-btn-ghost"
              data-testid="new-acquisition-pipeline-btn"
              title="Add Acquisition pipeline"
            ><Plus size={14} /></button>
          </div>
          <div className="space-y-1">
            {acquisitionPipelines.length === 0 && (
              <div className="mono text-[11px] text-mute px-3 py-2">No acquisition pipelines yet.</div>
            )}
            {acquisitionPipelines.map(renderRow)}
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm mono uppercase text-mute tracking-wider">Growth Pipelines</h3>
            <button
              onClick={() => newPipeline("growth")}
              className="pg-btn pg-btn-ghost"
              data-testid="new-growth-pipeline-btn"
              title="Add Growth pipeline"
            ><Plus size={14} /></button>
          </div>
          <div className="space-y-1">
            {growthPipelines.length === 0 && (
              <div className="mono text-[11px] text-mute px-3 py-2">No growth pipelines yet.</div>
            )}
            {growthPipelines.map(renderRow)}
          </div>
        </div>
      </div>

      <div className="col-span-3 pg-card p-6">
        <div className="flex items-end justify-between mb-5">
          <div className="flex-1 max-w-md">
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Pipeline Name</label>
            <input className="pg-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <div className="mono text-[10px] text-mute mt-2 uppercase tracking-wider" data-testid="pipeline-kind-label">
              {(draft.pipeline_kind || "acquisition") === "growth"
                ? `Growth pipeline${draft.growth_kind ? ` · ${draft.growth_kind}` : ""}`
                : "Acquisition pipeline"}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="pg-btn pg-btn-primary" data-testid="save-pipeline-btn"><Save size={14} /> Save</button>
            {pipelines.length > 1 && <button onClick={deletePipeline} className="pg-btn pg-btn-danger" data-testid="delete-pipeline-btn"><Trash2 size={14} /></button>}
          </div>
        </div>

        <div className="mono text-[11px] text-mute uppercase tracking-wider mb-2">Stages</div>
        <div className="space-y-2">
          {draft.stages.map((s, i) => (
            <div key={i} className="flex items-center gap-2 p-2" style={{ background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)" }}>
              <div className="flex flex-col">
                <button className="text-mute hover:text-white" onClick={() => moveStage(i, -1)} aria-label="up">▲</button>
                <button className="text-mute hover:text-white" onClick={() => moveStage(i, 1)} aria-label="down">▼</button>
              </div>
              <GripVertical size={14} className="text-mute" />
              <input
                className="pg-input flex-1"
                value={s.name}
                onChange={(e) => {
                  const arr = [...draft.stages]; arr[i] = { ...arr[i], name: e.target.value };
                  setDraft({ ...draft, stages: arr });
                }}
                data-testid={`stage-name-${i}`}
              />
              <input
                className="pg-input"
                style={{ width: 100 }}
                type="number" min="0" max="100"
                value={s.probability}
                onChange={(e) => {
                  const arr = [...draft.stages]; arr[i] = { ...arr[i], probability: e.target.value };
                  setDraft({ ...draft, stages: arr });
                }}
                data-testid={`stage-prob-${i}`}
              />
              <span className="text-mute mono text-xs">%</span>
              <button onClick={() => removeStage(i)} className="pg-btn pg-btn-danger"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button onClick={addStage} className="pg-btn mt-3" data-testid="add-stage-btn"><Plus size={14} /> Add Stage</button>
      </div>
    </div>
  );
}

const FIELDS_SUBTABS = [
  { k: "dispositions", label: "Lead Statuses" },
  { k: "sources", label: "Lead Sources" },
  { k: "move_types", label: "Move Types" },
  { k: "lost_reasons", label: "Lost Reasons" },
  { k: "custom_fields", label: "Custom Fields" },
];

function FieldsTab() {
  const [sub, setSub] = useState("dispositions");
  return (
    <div data-testid="fields-tab">
      <div className="mb-5">
        <h2 className="text-lg" style={{ fontWeight: 600 }}>Fields</h2>
        <div className="mono text-[11px] text-mute mt-1">
          Manage the pickers and structured data used across the app.
        </div>
      </div>
      <div
        className="flex items-center gap-1 mb-6"
        style={{
          borderBottom: "1px solid var(--pg-border)",
          paddingBottom: "0.25rem",
        }}
        data-testid="fields-subtabs"
      >
        {FIELDS_SUBTABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setSub(t.k)}
            data-testid={`fields-subtab-${t.k}`}
            className="pg-btn pg-btn-ghost"
            style={{
              padding: "0.4rem 0.9rem",
              fontSize: "0.8rem",
              borderRadius: "var(--r-pill)",
              color: sub === t.k ? "var(--pg-accent)" : "var(--pg-text-dim)",
              background: sub === t.k ? "rgba(110,240,220,0.08)" : "transparent",
              fontWeight: sub === t.k ? 600 : 500,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {sub === "dispositions" && <DispositionsTab />}
      {sub === "sources" && <SourcesTab />}
      {sub === "move_types" && <MoveTypesTab />}
      {sub === "lost_reasons" && <LostReasonsTab />}
      {sub === "custom_fields" && <CustomFieldsTab />}
    </div>
  );
}

function DispositionsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/dispositions").then(r => setItems(r.data)); }, []);
  return (
    <div>
      <h2 className="text-lg mb-1">Lead Disposition Statuses</h2>
      <div className="mono text-[11px] text-mute mb-5">Fixed list in v1. Editable in a future release.</div>
      <div className="pg-card p-6 max-w-xl space-y-2">
        {items.map(i => (
          <div key={i.key} className="flex items-center justify-between">
            <span>{i.label}</span>
            <span className={`pg-badge pg-badge-${i.color}`}>{i.color}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourcesTab() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");

  const load = async () => { const { data } = await api.get("/sources"); setItems(data); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/sources", { name: name.trim() }); setName(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async (s) => {
    if (!await confirm({
      title: `Delete "${s.name}"?`,
      message: "Existing leads keep this source label, but it won't be selectable for new leads.",
      destructive: true, confirmLabel: "Delete source",
    })) return;
    try { await api.delete(`/sources/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="max-w-xl" data-testid="sources-tab">
      <h2 className="text-lg mb-1">Lead Sources</h2>
      <div className="mono text-[11px] text-mute mb-5">Defaults: Website, Referral, Cold Outreach, Event, Social Media, Partner, Other.</div>
      <div className="pg-card p-6 space-y-2">
        {items.map(s => (
          <div key={s.id} className="flex items-center justify-between" data-testid={`source-row-${s.id}`}>
            <span>{s.name}</span>
            <button onClick={() => del(s)} className="pg-btn pg-btn-danger" data-testid={`source-delete-${s.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-3" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <input className="pg-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New source" data-testid="source-input-name" />
          <button onClick={add} className="pg-btn pg-btn-primary" data-testid="source-add-btn"><Plus size={14} /></button>
        </div>
      </div>
    </div>
  );
}

function LostReasonsTab() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/lost-reasons?include_hidden=true");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await api.post("/lost-reasons", form);
      toast.success("Reason added");
      setForm({ title: "", description: "" }); setShowForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    setSaving(false);
  };

  const toggleHidden = async (r) => {
    try { await api.patch(`/lost-reasons/${r.id}`, { hidden: !r.hidden }); load(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (r) => {
    if (r.is_default) {
      toast.error("Defaults can't be deleted — toggle hide instead.");
      return;
    }
    if (!await confirm({ title: `Delete "${r.title}"?`, message: "This is permanent.", destructive: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/lost-reasons/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const defaults = items.filter((r) => r.is_default);
  const custom = items.filter((r) => !r.is_default);

  return (
    <div className="max-w-3xl" data-testid="lost-reasons-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg">Lost Reasons</h2>
          <div className="mono text-[11px] text-mute mt-1">
            Shown when a deal is marked Lost. Defaults can be hidden; custom reasons can be deleted.
          </div>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="pg-btn pg-btn-primary" data-testid="lost-reason-new-btn">
          <Plus size={14} /> Add Reason
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="pg-card p-5 mb-5 space-y-3" data-testid="lost-reason-form">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Title <span style={{ color: "var(--pg-accent)" }}>*</span></label>
            <input className="pg-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="lost-reason-input-title" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Description</label>
            <textarea className="pg-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="lost-reason-input-description" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="pg-btn" onClick={() => { setShowForm(false); setForm({ title: "", description: "" }); }}>Cancel</button>
            <button type="submit" className="pg-btn pg-btn-primary" disabled={saving} data-testid="lost-reason-submit">{saving ? "Saving…" : "Create"}</button>
          </div>
        </form>
      )}

      {defaults.length > 0 && (
        <div className="mb-6">
          <div className="mono text-[11px] text-mute uppercase tracking-wider mb-2">Defaults</div>
          <div className="pg-card divide-y" style={{ borderColor: "var(--pg-border)" }}>
            {defaults.map((r) => (
              <ReasonRow key={r.id} r={r} onToggleHidden={() => toggleHidden(r)} />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="mono text-[11px] text-mute uppercase tracking-wider mb-2">Custom</div>
        {custom.length === 0 ? (
          <div className="pg-card p-5">
            <div className="text-mute mono text-xs">No custom reasons yet. Click "Add Reason" above.</div>
          </div>
        ) : (
          <div className="pg-card divide-y" style={{ borderColor: "var(--pg-border)" }}>
            {custom.map((r) => (
              <ReasonRow key={r.id} r={r} onDelete={() => remove(r)} onToggleHidden={() => toggleHidden(r)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReasonRow({ r, onDelete, onToggleHidden }) {
  return (
    <div className="flex items-start justify-between p-4" data-testid={`lost-reason-row-${r.id}`}>
      <div className="flex-1 min-w-0 pr-4">
        <div className="flex items-center gap-2">
          <span style={{ color: r.hidden ? "var(--pg-text-mute)" : "var(--pg-text)", fontWeight: 600 }}>{r.title}</span>
          {r.hidden && (
            <span
              className="mono uppercase"
              style={{
                fontSize: 10,
                color: "var(--pg-text-mute)",
                background: "var(--pg-surface-3)",
                border: "1px solid var(--pg-border)",
                padding: "1px 6px",
                borderRadius: "999px",
                letterSpacing: "0.05em",
              }}
            >
              hidden
            </span>
          )}
        </div>
        {r.description && (
          <div className="mono text-[11px] text-mute mt-1" style={{ lineHeight: 1.55 }}>{r.description}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleHidden}
          className="pg-btn"
          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
          data-testid={`lost-reason-toggle-${r.id}`}
        >
          {r.hidden ? "Show" : "Hide"}
        </button>
        {onDelete && (
          <button
            onClick={onDelete}
            className="pg-btn pg-btn-danger"
            style={{ padding: "0.35rem 0.55rem" }}
            data-testid={`lost-reason-delete-${r.id}`}
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function MoveTypesTab() {
  const confirm = useConfirm();
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");

  const load = async () => { const { data } = await api.get("/move-types"); setTypes(data); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/move-types", { name: name.trim() }); setName(""); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (t) => {
    if (!await confirm({
      title: `Delete "${t.name}"?`,
      message: "Existing moves keep this type label, but it won't be selectable for new moves.",
      destructive: true, confirmLabel: "Delete type",
    })) return;
    try { await api.delete(`/move-types/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="max-w-xl" data-testid="move-types-tab">
      <h2 className="text-lg mb-1">Move Types</h2>
      <div className="mono text-[11px] text-mute mb-5">Defaults: Call, Email, Meeting, Follow Up, Other.</div>
      <div className="pg-card p-6 space-y-2">
        {types.map(t => (
          <div key={t.id} className="flex items-center justify-between" data-testid={`move-type-row-${t.id}`}>
            <span>{t.name}</span>
            <button onClick={() => del(t)} className="pg-btn pg-btn-danger" data-testid={`move-type-delete-${t.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-3" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <input className="pg-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New move type" data-testid="move-type-input-name" />
          <button onClick={add} className="pg-btn pg-btn-primary" data-testid="move-type-add-btn"><Plus size={14} /></button>
        </div>
      </div>
    </div>
  );
}

function CustomFieldsTab() {
  const confirm = useConfirm();
  const [entity, setEntity] = useState("company");
  const [fields, setFields] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/custom-fields?entity=${entity}`);
      setFields(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity]);

  const move = async (idx, dir) => {
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= fields.length) return;
    const reordered = [...fields];
    [reordered[idx], reordered[nextIdx]] = [reordered[nextIdx], reordered[idx]];
    setFields(reordered);
    try { await api.post("/custom-fields/reorder", { ids: reordered.map(f => f.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const remove = async (f) => {
    if (!await confirm({ title: `Delete custom field "${f.label}"?`, message: "Historical data on records is kept; the field is hidden from new entries.", destructive: true, confirmLabel: "Delete field" })) return;
    try { await api.delete(`/custom-fields/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (f) => {
    try { await api.patch(`/custom-fields/${f.id}`, { active: !f.active }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg">Custom Fields</h2>
        <button className="pg-btn pg-btn-primary" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-custom-field-btn">
          <Plus size={14} /> Add Field
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setEntity("company")} className={`pg-btn ${entity === "company" ? "pg-btn-active" : ""}`} data-testid="cf-tab-company">Company Fields</button>
        <button onClick={() => setEntity("contact")} className={`pg-btn ${entity === "contact" ? "pg-btn-active" : ""}`} data-testid="cf-tab-contact">Contact Fields</button>
      </div>

      {fields.length === 0 ? (
        <div className="empty-state">No custom fields yet for {entity}. Max 20 per entity.</div>
      ) : (
        <div className="pg-card overflow-hidden">
          <table className="pg-table">
            <thead>
              <tr><th></th><th>Label</th><th>Type</th><th>Required</th><th>Active</th><th></th></tr>
            </thead>
            <tbody>
              {fields.map((f, idx) => (
                <tr key={f.id} data-testid={`cf-row-${f.id}`}>
                  <td>
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => move(idx, -1)} className="pg-btn pg-btn-ghost" style={{ padding: 2 }} title="Move up">▲</button>
                      <button onClick={() => move(idx, +1)} className="pg-btn pg-btn-ghost" style={{ padding: 2 }} title="Move down">▼</button>
                    </div>
                  </td>
                  <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{f.label}</td>
                  <td className="mono text-xs">{f.field_type}{f.field_type === "dropdown" && f.options?.length ? ` (${f.options.length})` : ""}</td>
                  <td>{f.required ? <span className="pg-badge pg-badge-yellow">required</span> : <span className="text-mute">optional</span>}</td>
                  <td>
                    <button onClick={() => toggleActive(f)} className={`pg-btn ${f.active ? "pg-btn-active" : ""}`} style={{ padding: "0.25rem 0.6rem", fontSize: 11 }} data-testid={`cf-active-${f.id}`}>
                      {f.active ? "Active" : "Hidden"}
                    </button>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditing(f); setShowForm(true); }} className="pg-btn" style={{ padding: "0.25rem 0.6rem", fontSize: 11 }} data-testid={`cf-edit-${f.id}`}>Edit</button>
                      <button onClick={() => remove(f)} className="pg-btn pg-btn-danger" style={{ padding: "0.25rem 0.6rem" }} data-testid={`cf-delete-${f.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <CustomFieldForm
          entity={entity}
          existing={editing}
          onClose={() => setShowForm(false)}
          onDone={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function CustomFieldForm({ entity, existing, onClose, onDone }) {
  const [form, setForm] = useState({
    label: existing?.label || "",
    field_type: existing?.field_type || "text",
    options: existing?.options || [],
    required: existing?.required || false,
    active: existing?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error("Label is required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.field_type !== "dropdown") payload.options = [];
      if (existing) {
        await api.patch(`/custom-fields/${existing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/custom-fields", { ...payload, entity });
        toast.success("Created");
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  const addOption = () => setForm({ ...form, options: [...form.options, ""] });
  const updateOption = (i, v) => {
    const next = [...form.options]; next[i] = v;
    setForm({ ...form, options: next });
  };
  const removeOption = (i) => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="cf-form-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">{existing ? "Edit Custom Field" : "New Custom Field"} · {entity}</h2>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Label</label>
            <input className="pg-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required data-testid="cf-input-label" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Type</label>
            <PragmaSelect
              value={form.field_type}
              onChange={(v) => setForm({ ...form, field_type: v })}
              options={[
                { value: "text",     label: "Text" },
                { value: "number",   label: "Number" },
                { value: "date",     label: "Date" },
                { value: "dropdown", label: "Dropdown" },
                { value: "url",      label: "URL" },
                { value: "checkbox", label: "Checkbox" },
              ]}
              testId="cf-input-type"
            />
          </div>
          {form.field_type === "dropdown" && (
            <div>
              <label className="block mono text-[11px] text-mute mb-2 uppercase tracking-wider">Dropdown Options</label>
              <div className="space-y-2">
                {form.options.map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <input className="pg-input" value={o} onChange={(e) => updateOption(i, e.target.value)} data-testid={`cf-option-${i}`} />
                    <button type="button" onClick={() => removeOption(i)} className="pg-btn pg-btn-danger" style={{ padding: "0.4rem" }}><Trash2 size={12} /></button>
                  </div>
                ))}
                <button type="button" onClick={addOption} className="pg-btn" data-testid="cf-add-option"><Plus size={12} /> Add Option</button>
              </div>
            </div>
          )}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} data-testid="cf-input-required" />
              <span>Required</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="cf-input-active" />
              <span>Active</span>
            </label>
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="cf-submit">{saving ? "Saving…" : (existing ? "Save" : "Create")}</button>
        </div>
      </div>
    </>
  );
}


/* ─── Integrations Tab (Gmail sync) ─────────────────────────────── */
function IntegrationsTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCeleb, setShowCeleb] = useState(false);
  const [showGmailSettings, setShowGmailSettings] = useState(false);
  const [autoFocusSig, setAutoFocusSig] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/status");
      setStatus(data);
      return data;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    load();
    // Show celebration or error toast if we've just returned from an OAuth redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      setShowCeleb(true);
      window.history.replaceState({}, "", window.location.pathname + "?tab=integrations");
    } else if (params.get("google") === "error") {
      toast.error(`Gmail connection failed (${params.get("reason") || "unknown"})`);
      window.history.replaceState({}, "", window.location.pathname + "?tab=integrations");
    }
    // Deep-link: /settings?tab=integrations#gmail-settings auto-opens the Gmail
    // settings modal (used from the Profile panel's signature link).
    if (window.location.hash === "#gmail-settings") {
      openSettings(true);
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/google/start");
      window.location.href = data.url;
    } catch (e) {
      setBusy(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start Google OAuth");
    }
  };

  const openSettings = (focusSignature = false) => {
    setAutoFocusSig(focusSignature);
    setShowGmailSettings(true);
  };

  const handleCelebSetupSignature = () => {
    setShowCeleb(false);
    // Small delay so the celebration exit animation completes before the side panel slides in
    setTimeout(() => openSettings(true), 260);
  };

  if (!status) return <div className="text-sm" style={{ color: "var(--pg-text-mute)" }}>Loading…</div>;
  const g = status.google || {};

  return (
    <div className="max-w-2xl">
      <h3 className="mb-4" style={{ fontSize: 18 }}>Email accounts</h3>
      <p className="text-sm mb-6" style={{ color: "var(--pg-text-mute)" }}>
        Connect your inbox so that emails you exchange with contacts &amp; leads appear
        automatically in their history timeline. Only messages whose sender or recipient
        matches an existing CRM record are stored — random personal email never enters Pragma.
      </p>

      {/* Gmail card */}
      <div className="pg-card p-5 mb-4" data-testid="integration-gmail-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div style={{
              width: 42, height: 42, borderRadius: 10, background: "rgba(234,67,53,0.10)",
              color: "#EA4335", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Mail size={20} />
            </div>
            <div>
              <div style={{ fontSize: 15 }}>Gmail</div>
              {g.connected ? (
                <div className="text-sm mt-1" style={{ color: "var(--pg-text-dim)" }}>
                  <CheckCircle2 size={13} style={{ display: "inline", marginRight: 4, color: "var(--pg-lime, #6aa84f)" }} />
                  Connected as <strong>{g.account?.email}</strong>
                  {g.account?.last_sync_at && (
                    <span style={{ color: "var(--pg-text-mute)" }}>
                      {" · last sync "}{new Date(g.account.last_sync_at).toLocaleString()}
                    </span>
                  )}
                </div>
              ) : g.configured ? (
                <div className="text-sm mt-1" style={{ color: "var(--pg-text-mute)" }}>
                  Not connected. Sync incoming + outgoing email into contact history.
                </div>
              ) : (
                <div className="text-sm mt-1" style={{ color: "var(--pg-red, #d93025)" }}>
                  <AlertCircle size={13} style={{ display: "inline", marginRight: 4 }} />
                  Not configured on this deployment. Ask your platform admin to add Google credentials.
                </div>
              )}
              {g.account?.status === "reauthorization_required" && (
                <div className="text-sm mt-2" style={{ color: "var(--pg-red, #d93025)" }}>
                  <AlertCircle size={13} style={{ display: "inline", marginRight: 4 }} />
                  Reconnect required — Google refresh token was revoked.
                </div>
              )}
            </div>
          </div>
          <div className="flex-shrink-0">
            {g.connected ? (
              <button
                onClick={() => openSettings(false)}
                className="pg-btn"
                data-testid="integration-gmail-settings"
                title="Manage Gmail settings"
              >
                <SettingsIcon size={14} strokeWidth={2.2} /> Settings
              </button>
            ) : (
              <button
                onClick={connect}
                className="pg-btn pg-btn-primary"
                disabled={busy || !g.configured}
                data-testid="integration-gmail-connect"
              >
                {busy ? "Opening…" : "Connect Gmail"}
              </button>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs mt-4" style={{ color: "var(--pg-text-mute)" }}>
        Pragma requests read + send access to Gmail. Sync runs every 5 minutes.
        Only email involving contacts, leads, deals or companies already in your CRM is stored.
      </p>

      <GmailConnectedCelebration
        open={showCeleb}
        accountEmail={g.account?.email}
        onSetupSignature={handleCelebSetupSignature}
        onDismiss={() => setShowCeleb(false)}
      />
      <GmailSettingsModal
        open={showGmailSettings}
        account={g.account}
        autoFocusSignature={autoFocusSig}
        onClose={() => setShowGmailSettings(false)}
        onDisconnected={load}
      />
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════
// Delete Account (Admin only) — permanently wipes the entire workspace
// ═══════════════════════════════════════════════════════════════════════
function DeleteAccountTab() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const startDelete = async () => {
    // Step 1: intent confirmation
    const goOn = await confirm({
      title: "Delete this Pragma workspace forever?",
      message:
        "This action cannot be undone. You will lose access to every lead, contact, company, deal, email log, deliverable and pipeline in this workspace, and every teammate will be signed out immediately.\n\nContinue?",
      confirmLabel: "I understand — continue",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!goOn) return;

    // Step 2: password re-entry
    const password = await confirm({
      title: "Confirm with your password",
      message: `Enter the password for ${user?.email} to permanently delete this workspace.`,
      confirmLabel: "Delete workspace",
      cancelLabel: "Cancel",
      destructive: true,
      input: { type: "password", placeholder: "Your Pragma password", required: true, label: "Password" },
    });
    if (!password) return;

    // Step 3: fire delete
    setBusy(true);
    try {
      await api.post("/tenant/delete", { password });
      // Success — nuke local session and boot to login
      localStorage.removeItem("pragma_token");
      toast.success("Workspace deleted. You've been signed out.");
      // Small delay so the toast is visible before the reload
      setTimeout(() => {
        window.location.href = "/login";
      }, 400);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }} data-testid="delete-account-tab">
      <div
        className="pg-card"
        style={{
          padding: "24px 28px",
          borderColor: "rgba(220,38,38,0.35)",
          background: "rgba(220,38,38,0.03)",
        }}
      >
        <div className="flex items-start gap-4">
          <div style={{
            width: 40, height: 40, flexShrink: 0, borderRadius: "50%",
            background: "rgba(220,38,38,0.12)", color: "var(--pg-red, #dc2626)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <AlertTriangle size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="text-lg" style={{ color: "var(--pg-text)", margin: 0 }}>Delete this account</h2>
            <p
              className="text-sm"
              style={{ color: "var(--pg-text-mute)", marginTop: 8, lineHeight: 1.6 }}
              data-testid="delete-account-copy"
            >
              Deleting your Pragma CRM account cannot be undone. You will lose access to all data.
              Please use caution before deleting your account.
            </p>
            <p
              className="text-xs"
              style={{ color: "var(--pg-text-mute)", marginTop: 10, lineHeight: 1.55 }}
            >
              If you have an active paid subscription, please cancel it in <strong>Settings → Billing</strong> before deleting.
            </p>
            <button
              onClick={startDelete}
              disabled={busy}
              className="pg-btn pg-btn-danger"
              style={{ marginTop: 18 }}
              data-testid="delete-account-btn"
            >
              <Trash2 size={14} /> {busy ? "Deleting…" : "Delete Account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
